import React, { useState, useRef } from 'react';
import { 
  User, Lock, Eye, EyeOff, ArrowRight, Loader2, 
  CheckCircle, AlertCircle, MapPin, Sparkles
} from 'lucide-react';

const DandiyaIcon = ({ className = "w-10 h-10" }) => (
  <svg viewBox="0 0 64 64" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    {/* First Stick */}
    <g transform="rotate(-30 32 32)">
      <rect x="29" y="4" width="6" height="56" rx="3" fill="#D97706" stroke="#FEF08A" strokeWidth="1" />
      <rect x="29" y="10" width="6" height="4" fill="#DC2626" />
      <rect x="29" y="18" width="6" height="4" fill="#16A34A" />
      <rect x="29" y="26" width="6" height="4" fill="#F59E0B" />
      <rect x="29" y="34" width="6" height="4" fill="#DC2626" />
      <circle cx="32" cy="7" r="4" fill="#FDE047" />
    </g>
    {/* Second Stick */}
    <g transform="rotate(30 32 32)">
      <rect x="29" y="4" width="6" height="56" rx="3" fill="#B45309" stroke="#FEF08A" strokeWidth="1" />
      <rect x="29" y="12" width="6" height="4" fill="#DC2626" />
      <rect x="29" y="20" width="6" height="4" fill="#16A34A" />
      <rect x="29" y="28" width="6" height="4" fill="#F59E0B" />
      <rect x="29" y="36" width="6" height="4" fill="#DC2626" />
      <circle cx="32" cy="7" r="4" fill="#FDE047" />
    </g>
  </svg>
);

const DiyaIcon = ({ className = "w-8 h-8" }) => (
  <svg viewBox="0 0 64 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <path 
      d="M6 24C6 37.25 17.65 44 32 44C46.35 44 58 37.25 58 24C46 27 18 27 6 24Z" 
      fill="url(#diyaGold)" 
      stroke="#F59E0B" 
      strokeWidth="1.5" 
    />
    <path d="M12 25C20 28 44 28 52 25" stroke="#FDE68A" strokeWidth="1" strokeDasharray="2 2" />
    <path 
      d="M32 3C32 3 24 13 24 20C24 24.4 27.6 28 32 28C36.4 28 40 24.4 40 20C40 13 32 3 32 3Z" 
      fill="url(#flameGradient)" 
      className="animate-pulse" 
    />
    <path d="M32 9C32 9 27 15 27 19.5C27 22.2 29.2 24.5 32 24.5C34.8 24.5 37 22.2 37 19.5C37 15 32 9 32 9Z" fill="#FEF08A" />
    <defs>
      <linearGradient id="diyaGold" x1="6" y1="24" x2="58" y2="44" gradientUnits="userSpaceOnUse">
        <stop stopColor="#D97706" />
        <stop offset="0.5" stopColor="#F59E0B" />
        <stop offset="1" stopColor="#92400E" />
      </linearGradient>
      <linearGradient id="flameGradient" x1="32" y1="3" x2="32" y2="28" gradientUnits="userSpaceOnUse">
        <stop stopColor="#FEF08A" />
        <stop offset="0.4" stopColor="#F59E0B" />
        <stop offset="1" stopColor="#DC2626" />
      </linearGradient>
    </defs>
  </svg>
);

const ElectricNSGroup = () => (
  <div className="relative inline-flex flex-col select-none py-2 px-1">
    <style>{`
      @keyframes electricSurge {
        0% {
          stroke-dashoffset: 600;
          opacity: 0.7;
        }
        50% {
          stroke-dashoffset: 0;
          opacity: 1;
          filter: drop-shadow(0 0 8px #97D700) drop-shadow(0 0 16px #00f0ff);
        }
        100% {
          stroke-dashoffset: -600;
          opacity: 0.7;
        }
      }

      @keyframes plasmaSine {
        0% {
          transform: translateX(-25%) scaleY(1);
        }
        50% {
          transform: translateX(0%) scaleY(1.35) skewX(-2deg);
        }
        100% {
          transform: translateX(-25%) scaleY(1);
        }
      }

      @keyframes lightningStrike {
        0%, 92%, 100% {
          opacity: 0;
          transform: scale(0.95);
        }
        93% {
          opacity: 1;
          transform: scale(1.08) translate(1px, -1px);
        }
        95% {
          opacity: 0.2;
        }
        96% {
          opacity: 0.95;
          transform: scale(1.04) translate(-1px, 1px);
        }
        98% {
          opacity: 0;
        }
      }

      @keyframes highVoltageTremor {
        0%, 100% { transform: translate(0, 0); }
        20% { transform: translate(-0.8px, 0.6px); }
        40% { transform: translate(0.6px, -0.6px); }
        60% { transform: translate(-0.5px, -0.5px); }
        80% { transform: translate(0.8px, 0.4px); }
      }

      @keyframes currentPass {
        0% {
          background-position: -200% 0;
        }
        100% {
          background-position: 200% 0;
        }
      }

      @keyframes empRipple {
        0% {
          transform: scaleX(0.7) scaleY(0.4);
          opacity: 0.9;
        }
        70% {
          transform: scaleX(1.4) scaleY(1.8);
          opacity: 0.3;
        }
        100% {
          transform: scaleX(1.7) scaleY(2.2);
          opacity: 0;
        }
      }

      .animate-electric-surge {
        stroke-dasharray: 120 180;
        animation: electricSurge 2.2s linear infinite;
      }

      .animate-plasma-wave {
        animation: plasmaSine 1.8s ease-in-out infinite;
      }

      .animate-lightning {
        animation: lightningStrike 2.5s infinite;
      }

      .animate-voltage-tremor {
        animation: highVoltageTremor 0.12s infinite;
      }

      .electric-gradient-text {
        background: linear-gradient(
          90deg,
          #ffffff 0%,
          #e2ff85 20%,
          #00f0ff 45%,
          #ffffff 55%,
          #97D700 80%,
          #ffffff 100%
        );
        background-size: 250% 100%;
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        animation: currentPass 2.4s linear infinite;
      }
    `}</style>

    <div className="relative inline-block">
      <div 
        className="absolute inset-0 -m-2 rounded-full pointer-events-none blur-lg"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(151,215,0,0.35) 0%, rgba(0,240,255,0.2) 45%, transparent 75%)',
          animation: 'empRipple 2.2s ease-out infinite',
        }}
      />

      <span
        className="absolute inset-0 text-3xl font-black tracking-tight block leading-none pointer-events-none blur-[3px] select-none text-[#97D700] opacity-80 animate-voltage-tremor"
        style={{ fontFamily: 'Outfit' }}
        aria-hidden="true"
      >
        NS Group
      </span>

      <span
        className="relative text-3xl font-black tracking-tight block leading-none select-none electric-gradient-text drop-shadow-[0_0_8px_rgba(151,215,0,0.8)]"
        style={{ fontFamily: 'Outfit' }}
        data-testid="login-title"
      >
        NS Group
      </span>

      <svg
        className="absolute inset-0 w-full h-full pointer-events-none overflow-visible"
        viewBox="0 0 200 40"
        preserveAspectRatio="none"
      >
        <defs>
          <filter id="electric-glow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="1.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <path
          d="M -10 20 Q 25 2, 60 20 T 130 20 T 210 20"
          fill="none"
          stroke="#97D700"
          strokeWidth="2.2"
          strokeLinecap="round"
          filter="url(#electric-glow)"
          className="animate-electric-surge"
        />

        <g className="animate-plasma-wave">
          <path
            d="M -20 22 Q 15 34, 50 22 T 120 22 T 190 22 T 230 22"
            fill="none"
            stroke="#00f0ff"
            strokeWidth="1.4"
            strokeDasharray="15 25"
            opacity="0.85"
            filter="url(#electric-glow)"
          />
        </g>

        <path
          d="M 12 10 L 22 22 L 18 25 L 32 36 M 142 8 L 150 18 L 145 22 L 160 32"
          fill="none"
          stroke="#ffffff"
          strokeWidth="1.8"
          strokeLinecap="round"
          filter="url(#electric-glow)"
          className="animate-lightning"
        />
      </svg>
    </div>

    <span className="text-[10px] font-extrabold uppercase tracking-[0.25em] text-[#97D700] mt-1.5 block drop-shadow-[0_0_10px_rgba(151,215,0,0.6)] animate-pulse">
      Personnel Attendance Portal
    </span>
  </div>
);

const LoginForm = ({ onLogin, loading }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [isFetchingGps, setIsFetchingGps] = useState(false);

  // 3D Parallax Tilt State
  const cardRef = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0, shineX: 50, shineY: 50 });

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const rotateX = ((y - centerY) / centerY) * -5.0;
    const rotateY = ((x - centerX) / centerX) * 5.0;

    setTilt({
      x: rotateX,
      y: rotateY,
      shineX: (x / rect.width) * 100,
      shineY: (y / rect.height) * 100
    });
  };

  const handleMouseLeave = () => {
    setTilt({ x: 0, y: 0, shineX: 50, shineY: 50 });
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsFetchingGps(true);

    try {
      const getLocation = () => new Promise((resolve) => {
        if (!navigator.geolocation) {
          resolve(null);
          return;
        }
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            resolve({
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude,
              accuracy: pos.coords.accuracy,
              timestamp: pos.timestamp || Date.now()
            });
          },
          (err) => {
            console.warn("Location prompt dismissed or blocked:", err.message);
            resolve(null);
          },
          { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
        );
      });

      const locationData = await getLocation();

      const finalLoginData = {
        username: loginData.username.trim(),
        password: loginData.password,
        location: locationData
      };

      const result = await onLogin(finalLoginData);
      if (!result?.success) {
        setError(result?.error || "Login failed. Please verify credentials.");
      }
    } catch (err) {
      setError("An unexpected authentication error occurred.");
    } finally {
      setIsFetchingGps(false);
    }
  };

  const isSubmitDisabled = loading || isFetchingGps;

  return (
    <div className="min-h-screen w-full flex flex-col bg-[#050B08] text-white selection:bg-[#97D700] selection:text-black overflow-hidden relative font-sans" data-testid="login-page">
      
      {/* Ambient Lighting & Glow */}
      <div className="absolute top-[-10%] left-[-5%] w-[650px] h-[650px] bg-gradient-to-br from-emerald-600/30 via-[#97D700]/20 to-transparent rounded-full blur-[140px] pointer-events-none mix-blend-screen animate-pulse duration-[10000ms]" />
      <div className="absolute top-[2%] right-[10%] w-[500px] h-[500px] bg-gradient-to-bl from-amber-500/25 via-yellow-600/15 to-transparent rounded-full blur-[130px] pointer-events-none mix-blend-screen" />
      <div className="absolute bottom-[-15%] right-[25%] w-[600px] h-[600px] bg-gradient-to-tl from-emerald-800/25 via-teal-900/15 to-transparent rounded-full blur-[150px] pointer-events-none mix-blend-screen" />
      
      {/* Festive Navratri Top Header */}
      <header className="w-full z-30 pt-3 pb-2 px-6 bg-gradient-to-r from-emerald-950/80 via-emerald-900/60 to-emerald-950/80 border-b border-amber-400/20 backdrop-blur-md shadow-[0_4px_25px_rgba(217,119,6,0.15)] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Sparkles className="w-5 h-5 text-amber-300 animate-spin duration-[8000ms]" />
          <span className="text-xs uppercase tracking-[0.25em] font-extrabold text-amber-400/90 hidden sm:inline-block">
            Festive Edition
          </span>
        </div>

        <div className="flex items-center gap-3 md:gap-4 animate-in fade-in duration-700">
          <div className="hidden sm:block">
            <DiyaIcon className="w-7 h-7 filter drop-shadow-[0_0_8px_rgba(245,158,11,0.7)]" />
          </div>
          <h2 className="text-base sm:text-xl md:text-2xl font-serif font-bold italic tracking-wide text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-yellow-300 to-amber-100 drop-shadow-[0_2px_12px_rgba(245,158,11,0.6)]">
            Happy NAVRATRI to all NS family
          </h2>
          <DiyaIcon className="w-7 h-7 filter drop-shadow-[0_0_8px_rgba(245,158,11,0.7)]" />
        </div>

        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-[#97D700] animate-ping" />
          <span className="text-[11px] font-bold tracking-wider text-emerald-300 uppercase hidden sm:inline-block">
            Shubh Navratri
          </span>
        </div>
      </header>

      {/* Main Split Layout */}
      <div className="flex-1 flex w-full relative">
        <div className="absolute inset-0 bg-[radial-gradient(#10b981_1px,transparent_1px)] [background-size:28px_28px] opacity-15 pointer-events-none" />

        {/* LEFT AUTHENTICATION STAGE (GREEN THEMED) */}
        <div className="w-full lg:w-[560px] xl:w-[600px] flex flex-col justify-center px-6 sm:px-12 md:px-16 py-8 z-20 relative shrink-0">
          
          <div 
            ref={cardRef}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            style={{
              transform: `perspective(1100px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
              transition: 'transform 0.15s ease-out'
            }}
            className="relative rounded-[2.5rem] bg-gradient-to-b from-[#0e3b26]/95 via-[#072418]/95 to-[#04160f]/95 p-8 sm:p-10 border-2 border-emerald-500/40 shadow-[0_25px_80px_rgba(5,150,105,0.35),inset_0_1px_2px_rgba(110,231,183,0.35)] backdrop-blur-2xl overflow-hidden"
          >
            {/* Dandiya Sticks Festive Element */}
            <div className="absolute top-6 right-6 z-20 pointer-events-none drop-shadow-[0_6px_14px_rgba(0,0,0,0.65)]">
              <DandiyaIcon className="w-12 h-12 sm:w-14 sm:h-14 opacity-95" />
            </div>

            {/* Corner Decorative Rings */}
            <div className="absolute -top-10 -right-10 w-36 h-36 rounded-full border border-amber-400/15 pointer-events-none" />
            <div className="absolute -top-6 -right-6 w-28 h-28 rounded-full border border-emerald-400/20 pointer-events-none" />
            <div className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full border border-amber-400/15 pointer-events-none" />

            {/* Dynamic Surface Highlight */}
            <div 
              className="absolute inset-0 rounded-[2.5rem] pointer-events-none opacity-40 transition-opacity duration-300"
              style={{
                background: `radial-gradient(circle 320px at ${tilt.shineX}% ${tilt.shineY}%, rgba(167,243,208,0.18), transparent)`
              }}
            />

            {/* Header / Brand */}
            <div className="relative z-10 mb-8">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3.5">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-b from-emerald-400/20 to-emerald-950/60 border border-emerald-400/30 shadow-[0_8px_25px_rgba(4,120,87,0.4),inset_0_2px_4px_rgba(255,255,255,0.3)] flex items-center justify-center p-2.5 backdrop-blur-md transform hover:scale-105 transition-transform duration-300">
                    <img 
                      src="/logo.png" 
                      alt="NS Group" 
                      className="w-full h-full object-contain filter drop-shadow-[0_4px_8px_rgba(0,0,0,0.5)]"
                      onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }}
                    />
                  </div>
                  <ElectricNSGroup />
                </div>
              </div>

              <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight leading-tight" style={{ fontFamily: 'Outfit' }}>
                Welcome back
              </h1>
            </div>

            {/* Feedback Banners */}
            {error && (
              <div className="relative z-10 bg-rose-500/10 border border-rose-500/30 text-rose-300 p-4 rounded-2xl mb-6 text-xs sm:text-sm font-semibold flex items-start gap-3 shadow-[0_10px_25px_rgba(244,63,94,0.15)] animate-in fade-in slide-in-from-top-2" data-testid="auth-error">
                <AlertCircle className="w-5 h-5 shrink-0 text-rose-400 mt-0.5" />
                <p className="leading-snug">{error}</p>
              </div>
            )}
            {success && (
              <div className="relative z-10 bg-emerald-500/20 border border-emerald-400/40 text-emerald-200 p-4 rounded-2xl mb-6 text-xs sm:text-sm font-semibold flex items-start gap-3 shadow-[0_10px_25px_rgba(16,185,129,0.2)] animate-in fade-in slide-in-from-top-2" data-testid="auth-success">
                <CheckCircle className="w-5 h-5 shrink-0 text-emerald-300 mt-0.5" />
                <p className="leading-snug">{success}</p>
              </div>
            )}

            {/* Login Inputs */}
            <form onSubmit={handleLoginSubmit} className="relative z-10 space-y-5" data-testid="login-form">
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-200/90 ml-1 flex items-center justify-between">
                  <span>Account Username</span>
                </label>
                <div className="relative group">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 w-9 h-9 rounded-xl bg-white/5 border border-emerald-500/10 flex items-center justify-center text-emerald-300/60 group-focus-within:text-[#97D700] group-focus-within:border-[#97D700]/40 transition-all pointer-events-none">
                    <User className="w-4 h-4" />
                  </div>
                  <input 
                    type="text"
                    placeholder="Enter username or ID"
                    value={loginData.username}
                    onChange={e => setLoginData({...loginData, username: e.target.value})}
                    required
                    data-testid="login-username"
                    className="w-full bg-[#03150D]/90 border border-emerald-500/30 rounded-2xl py-4 pl-16 pr-4 text-sm font-semibold text-white placeholder:text-emerald-300/40 shadow-[inset_0_2px_4px_rgba(0,0,0,0.8),0_1px_1px_rgba(255,255,255,0.05)] focus:outline-none focus:border-[#97D700] focus:ring-4 focus:ring-[#97D700]/20 transition-all"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-200/90 ml-1">
                  Password
                </label>
                <div className="relative group">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 w-9 h-9 rounded-xl bg-white/5 border border-emerald-500/10 flex items-center justify-center text-emerald-300/60 group-focus-within:text-[#97D700] group-focus-within:border-[#97D700]/40 transition-all pointer-events-none">
                    <Lock className="w-4 h-4" />
                  </div>
                  <input 
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter security password"
                    value={loginData.password}
                    onChange={e => setLoginData({...loginData, password: e.target.value})}
                    required
                    data-testid="login-password"
                    className="w-full bg-[#03150D]/90 border border-emerald-500/30 rounded-2xl py-4 pl-16 pr-14 text-sm font-semibold text-white placeholder:text-emerald-300/40 shadow-[inset_0_2px_4px_rgba(0,0,0,0.8),0_1px_1px_rgba(255,255,255,0.05)] focus:outline-none focus:border-[#97D700] focus:ring-4 focus:ring-[#97D700]/20 transition-all"
                  />
                  <button 
                    type="button" 
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 p-2 rounded-xl text-emerald-300/60 hover:text-white hover:bg-emerald-500/10 transition-all cursor-pointer"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <button 
                type="submit" 
                disabled={isSubmitDisabled}
                data-testid="login-submit"
                className="w-full mt-6 py-4 px-6 rounded-2xl bg-gradient-to-r from-[#97D700] via-[#a6ec00] to-[#80bd00] text-slate-950 font-black text-xs uppercase tracking-[0.2em] shadow-[0_12px_35px_rgba(151,215,0,0.4),inset_0_1px_2px_rgba(255,255,255,0.6),inset_0_-3px_0_rgba(0,0,0,0.25)] border-b-2 border-emerald-800 hover:shadow-[0_18px_45px_rgba(151,215,0,0.6)] active:translate-y-1 active:border-b-0 active:shadow-[0_4px_15px_rgba(151,215,0,0.3)] transition-all flex items-center justify-center gap-2 group cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {isFetchingGps ? (
                  <>
                    <MapPin className="w-4 h-4 animate-bounce text-slate-950" />
                    <span>Getting your location...</span>
                  </>
                ) : loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin text-slate-950" />
                    <span>Securing Credentials...</span>
                  </>
                ) : (
                  <>
                    <span>Sign In</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1.5 transition-transform duration-300 text-slate-950" />
                  </>
                )}
              </button>
            </form>

            <div className="relative z-10 mt-6 text-center">
              <p className="text-[10px] font-bold text-emerald-400/60 uppercase tracking-[0.25em]">
                NS GROUP &copy; 2026 &bull; ALL RIGHTS RESERVED
              </p>
            </div>
          </div>
        </div>

        {/* RIGHT SHOWCASE */}
        <div className="hidden lg:flex flex-1 relative flex-col items-center justify-end pb-8 sm:pb-12 px-8 overflow-hidden bg-slate-950">
          <img 
            src="/login-bg.jpg" 
            alt="NS Group Capabilities Overview" 
            className="absolute inset-0 w-full h-full object-contain xl:object-cover opacity-85 filter-none transition-opacity duration-300" 
          />
          
          <div className="absolute inset-0 bg-gradient-to-r from-[#050B08] via-transparent to-transparent pointer-events-none w-1/3" />
          <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-[#050B08]/90 via-[#050B08]/40 to-transparent pointer-events-none" />
          
          <div className="relative z-10 max-w-lg w-full text-center space-y-2.5 p-5 sm:p-6 rounded-3xl bg-black/65 border border-amber-400/25 backdrop-blur-sm shadow-[0_20px_50px_rgba(0,0,0,0.7)]">
            <div className="flex justify-center -mt-0.5">
              <DiyaIcon className="w-9 h-9 filter drop-shadow-[0_0_12px_rgba(245,158,11,0.85)]" />
            </div>
            <h3 className="text-xl sm:text-2xl font-serif font-bold text-amber-200 tracking-wide drop-shadow-[0_2px_8px_rgba(245,158,11,0.4)]">
              Wishing You Prosperity & Success
            </h3>
            <p className="text-emerald-100/85 text-xs sm:text-sm leading-relaxed max-w-md mx-auto">
              May the divine blessings of Goddess Durga illuminate our endeavors across power, energy, and global automation.
            </p>
          </div>
        </div>

      </div>
    </div>
  );
};

export default LoginForm;